import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";

/**
 * Notifications list — in-app inbox for booking confirmations & other events.
 * Reuses the same auth + axios pattern as the rest of the app; no new
 * dependencies. Clicking a notification marks it read and navigates to its
 * link (usually the related booking).
 */
export default function Notifications() {
  useSeo({ title: "Notifications — BookFreely", path: "/notifications", noindex: true });
  const { authHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () =>
    axios.get(`${API}/notifications?limit=50`, { headers: authHeader() })
      .then((r) => setItems(r.data.notifications || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line
  }, []);

  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/read-all`, {}, { headers: authHeader() }); load(); } catch { /* noop */ }
  };
  const openOne = async (n) => {
    if (!n.read) {
      try { await axios.post(`${API}/notifications/${n.id}/read`, {}, { headers: authHeader() }); } catch { /* noop */ }
    }
    if (n.link) nav(n.link);
  };

  const anyUnread = items.some((n) => !n.read);

  return (
    <div className="App" data-testid="notifications-page">
      <Header hideSearch />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Inbox</div>
            <h1 className="font-display text-3xl mt-1" style={{ color: "var(--gf-text)" }}>Notifications</h1>
          </div>
          {anyUnread && (
            <button data-testid="notif-mark-all-read" onClick={markAllRead} className="text-xs hover:underline" style={{ color: "var(--gf-accent)" }}>
              Mark all as read
            </button>
          )}
        </div>
        {loading && <div className="mt-6 text-sm" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="mt-6 rounded-2xl border p-6 text-sm" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)", color: "var(--gf-text-muted)" }}>
            You have no notifications yet. When a booking is confirmed or a business responds, it will show up here.
          </div>
        )}
        <div className="mt-6 space-y-2">
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-${n.id}`}
              onClick={() => openOne(n)}
              className="w-full text-left rounded-xl border p-4 hover:opacity-90 transition-opacity"
              style={{
                borderColor: n.read ? "var(--gf-border)" : "var(--gf-accent)",
                background: n.read ? "var(--gf-surface)" : "var(--gf-surface-2)",
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm" style={{ color: "var(--gf-text)" }}>{n.title}</div>
                {!n.read && <span className="inline-block w-2 h-2 rounded-full mt-1.5 flex-none" style={{ background: "var(--gf-accent)" }} aria-label="unread" />}
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>{n.body}</div>
              <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
        <div className="mt-6 text-xs">
          <Link to="/" className="hover:underline" style={{ color: "var(--gf-text-muted)" }}>← Back to home</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
